// Cours premium Plateau n°2 — "Tu stabilises • Tu coordonnes • Tu diriges • Tu gagnes en précision"
// Plateau A2 — 14 chapitres pédagogiques (sans visuels, trame identique aux autres cours premium)
// Session 2 : transformer les découvertes de la session 1 en coordination, précision et stabilité

export const cours = {
  slug: 'deuxieme-plateau-a2',
  title: 'Ma 2ᵉ séance plateau A2',
  subtitle: 'Tu stabilises • Tu coordonnes • Tu diriges • Tu gagnes en précision',
  duree: '2 heures — plateau école',
  cover: '/images/cours/plateau/session-2.png',
  mantra: 'REGARDE → STABILISE → DOSE → DIRIGE → ENCHAÎNE',
  intro: "Lors de ta première séance, tu as découvert comment ta moto réagit à tes actions. Aujourd'hui, nous allons chercher à rendre tes gestes plus précis, plus réguliers et moins hésitants. Je ne te demande toujours pas de réciter ou de reproduire mécaniquement un parcours : je veux que tu comprennes ce qui te permet de réussir.",
  sections: [
    {
      num: '01',
      kicker: 'Point de départ',
      title: 'Ton bilan n°1 devient ton point de départ',
      lead: "Avant de repartir, nous reprenons rapidement ta première séance. Tu ne repars pas de zéro : tu reprends là où tu t'es arrêté.",
      type: 'list',
      list: [
        { label: 'DÉJÀ PLUS FACILE', desc: 'Qu\'est-ce qui te paraît déjà plus naturel qu\'en début de séance 1 ?' },
        { label: 'ENCORE DIFFICILE', desc: 'Qu\'est-ce qui te demande encore beaucoup d\'attention ?' },
        { label: 'PERTE D\'ÉQUILIBRE', desc: 'À quel moment perds-tu le plus facilement ton équilibre ?' },
        { label: 'COMMANDE FRAGILE', desc: 'Quelle commande te pose encore problème ?' },
      ],
      retenir: 'TON PREMIER OBJECTIF AUJOURD\'HUI : RETROUVER RAPIDEMENT TES SENSATIONS.',
    },
    {
      num: '02',
      kicker: 'Réactivation',
      title: 'Tu retrouves ta moto sans tout recommencer',
      lead: "Installe-toi. Retrouve tes commandes. Démarre. Effectue quelques déplacements simples. Je t'aide si nécessaire, mais moins que pendant la première séance. Une compétence commence à être acquise lorsque tu es capable de la retrouver sans que je te redonne toute la procédure.",
      type: 'list',
      list: [
        { label: 'POSITION', desc: 'Installation retrouvée sans hésitation.' },
        { label: 'REGARD', desc: 'Il repart tout de suite haut et loin.' },
        { label: 'POINT DE PATINAGE', desc: 'Repéré dès les premiers mètres.' },
        { label: 'MOTRICITÉ', desc: 'Douce et continue, sans à-coup.' },
        { label: 'FREIN ARRIÈRE', desc: 'Utilisé avec finesse à basse allure.' },
        { label: 'DÉMARRAGES / ARRÊTS', desc: 'Nets, choisis, sans hésitation entre les commandes.' },
      ],
      retenir: 'TU NE RECOMMENCES PAS TON APPRENTISSAGE. TU RÉACTIVES TES ACQUIS.',
    },
    {
      num: '03',
      kicker: 'Allure lente stable',
      title: 'Tu stabilises ton allure lente — doucement sur les gaz',
      lead: "Je vais être plus exigeant. Je ne veux plus seulement que tu avances lentement : je veux que ta moto avance lentement, régulièrement et sans à-coups. On reprend la notion de la leçon 1 — « doucement sur les gaz » : ton poignet reste fin et progressif, jamais brusque. Si ta moto accélère puis ralentit constamment, cherche la cause :",
      type: 'list',
      list: [
        { label: 'ACCÉLÉRATEUR', desc: 'Est-ce que je joue trop avec l\'accélérateur ? (Rappel : doucement sur les gaz — pas d\'aller-retour.)' },
        { label: 'EMBRAYAGE', desc: 'Est-ce que mon embrayage manque de progressivité ?' },
        { label: 'MOTRICITÉ', desc: 'Est-ce que je coupe complètement ma motricité ?' },
        { label: 'CRISPATION', desc: 'Est-ce que je me crispe sur le guidon ?' },
      ],
      retenir: 'LENTEMENT + RÉGULIÈREMENT + SANS À-COUPS = DOUCEMENT SUR LES GAZ. LA CONTINUITÉ AVANT LA LENTEUR.',
    },
    {
      num: '04',
      kicker: 'Équilibre en phase',
      title: 'Tu coordonnes tes commandes — l\'équilibre en phase',
      lead: "À basse allure, plusieurs actions doivent fonctionner ensemble : accélérateur, embrayage, frein arrière, direction, regard. C'est ce qu'on appelle « l'équilibre en phase » (cf. leçon 1) : tes 6 commandes agissent comme un orchestre — ensemble, au même moment. L'objectif n'est plus seulement d'utiliser chaque commande correctement : tu apprends à les mettre en phase, sans devoir penser à chacune séparément.",
      type: 'cards3',
      cards: [
        { title: 'STABILISER', desc: 'Tu stabilises ton accélérateur, sans à-coup sur la poignée.' },
        { title: 'DOSER', desc: 'Tu doses la transmission avec ton embrayage.' },
        { title: 'AJUSTER', desc: 'Tu ajustes ton allure avec finesse au frein arrière.' },
      ],
      body: "Tes mains et ton pied travaillent. Mais ton regard continue à conduire — tu regardes toujours où tu veux aller, pas ce que tu es en train de faire avec tes commandes. C'est cela, être « en phase » : chaque commande à sa place, en même temps.",
      retenir: 'L\'ÉQUILIBRE EN PHASE : TES 6 COMMANDES JOUENT ENSEMBLE, PAS L\'UNE APRÈS L\'AUTRE.',
    },
    {
      num: '05',
      kicker: 'Regard qui anticipe',
      title: 'Ton regard prend de l\'avance',
      lead: "Pendant la première séance, tu as appris à lever les yeux. Maintenant, nous allons apprendre à préparer ta trajectoire avec ton regard. Ton regard ne doit plus s'arrêter sur la difficulté : il doit préparer ce qui vient après.",
      type: 'cards3',
      cards: [
        { title: 'JE REPÈRE', desc: 'Je repère la porte ou le changement de direction.' },
        { title: 'JE PARS PLUS LOIN', desc: 'Puis mon regard part immédiatement vers la suite.' },
        { title: 'JE PRÉPARE LA SORTIE', desc: 'Avant un demi-tour ou une courbe, je cherche déjà la sortie.' },
      ],
      retenir: 'TES YEUX DOIVENT ARRIVER AVANT TA MOTO.',
    },
    {
      num: '06',
      kicker: 'Direction basse allure',
      title: 'Tu diriges ta moto à basse allure',
      lead: "Nous augmentons progressivement les changements de direction. La moto doit pouvoir tourner sans que tu perdes ta motricité, ton équilibre, ton regard ou ta précision. Je vais particulièrement observer :",
      type: 'list',
      list: [
        { label: 'CRISPATION DES BRAS', desc: 'Bras souples ou verrouillés sur le guidon ?' },
        { label: 'REGARD', desc: 'Actif et anticipateur ou fixé sur le cône ?' },
        { label: 'ALLURE', desc: 'Régulière ou saccadée pendant le virage ?' },
        { label: 'COMMANDES', desc: 'Utilisées ensemble ou l\'une après l\'autre ?' },
        { label: 'CAPACITÉ À ORIENTER', desc: 'La moto suit-elle réellement ton intention ?' },
      ],
      retenir: 'SI TU FIXES LE CÔNE, TON REGARD EST DÉJÀ EN RETARD.',
    },
    {
      num: '07',
      kicker: 'Demi-tour précis',
      title: 'Tu resserres progressivement tes demi-tours',
      lead: "Nous reprenons le demi-tour, mais avec davantage de précision. Avant d'entrer : ralentis, stabilise ta motricité, prépare ton regard. Puis tourne la tête et cherche ta sortie. Si ton demi-tour s'élargit, nous cherchons la cause plutôt que le résultat.",
      type: 'list',
      list: [
        { label: 'REGARD TOURNÉ', desc: 'Ton regard est-il suffisamment tourné vers la sortie ?' },
        { label: 'MOTRICITÉ CONSERVÉE', desc: 'As-tu coupé complètement ta motricité en cours de manœuvre ?' },
        { label: 'BRAS DÉTENDUS', desc: 'Es-tu crispé sur le guidon ?' },
        { label: 'ALLURE ADAPTÉE', desc: 'Ton allure d\'entrée est-elle compatible avec le rayon voulu ?' },
        { label: 'PRÉPARATION AMONT', desc: 'As-tu préparé ton changement de direction suffisamment tôt ?' },
      ],
      retenir: 'NOUS NE CORRIGEONS PAS SEULEMENT LE RÉSULTAT. NOUS CHERCHONS LA CAUSE.',
    },
    {
      num: '08',
      kicker: 'Moto disponible',
      title: 'Tu apprends à laisser la moto bouger sous toi',
      lead: "À basse allure, tu peux avoir tendance à vouloir garder la moto parfaitement verticale. Mais pour changer de direction, elle doit pouvoir s'orienter et s'incliner. L'objectif n'est pas de te jeter d'un côté ou de l'autre : nous recherchons souplesse, équilibre, disponibilité et précision.",
      type: 'cards3',
      cards: [
        { title: 'BRAS DISPONIBLES', desc: 'Ils absorbent les mouvements sans les bloquer.' },
        { title: 'REGARD ACTIF', desc: 'Il continue à construire la trajectoire.' },
        { title: 'JAMBES STABILISATRICES', desc: 'Elles participent à ton équilibre sans se raidir.' },
      ],
      retenir: 'PLUS TU TE CRISPES, PLUS TU RENDS LA MOTO DIFFICILE À DIRIGER.',
    },
    {
      num: '09',
      kicker: 'Portes & enchaînements',
      title: 'Tu travailles les portes et les enchaînements',
      lead: "Le cône n'est pas ton objectif : il matérialise simplement une zone à franchir. Nous travaillons progressivement : une porte, puis deux portes, puis plusieurs changements de direction. Tu commences à comprendre que la réussite d'une difficulté dépend souvent de ce que tu as préparé juste avant.",
      type: 'timeline',
      steps: [
        { time: '1', label: 'JE REPÈRE', desc: 'J\'identifie la porte à franchir, sans m\'y accrocher visuellement.' },
        { time: '2', label: 'JE PRÉPARE', desc: 'Regard, allure et trajectoire ajustés en amont.' },
        { time: '3', label: 'JE PASSE', desc: 'Je franchis la porte avec précision, sans hésitation.' },
        { time: '4', label: 'JE REGARDE LA SUITE', desc: 'Mon regard est déjà sur la porte suivante.' },
      ],
      retenir: 'LE CÔNE N\'EST PAS TON OBJECTIF. IL MATÉRIALISE UNE ZONE À FRANCHIR.',
    },
    {
      num: '10',
      kicker: 'Arrêt maîtrisé',
      title: 'Tu maîtrises tes arrêts et tes redémarrages',
      lead: "S'arrêter correctement fait partie de la maîtrise de la moto. Tu dois être capable de préparer ton arrêt, de le placer où tu l'as décidé, puis de repartir proprement. Nous travaillons aussi la précision : je m'arrête où je l'ai décidé, pas un mètre avant, pas un mètre après.",
      type: 'timeline',
      steps: [
        { time: '1', label: 'ARRÊT PRÉPARÉ', desc: 'Regard, réduction d\'allure, freinage progressif choisis à l\'avance.' },
        { time: '2', label: 'STABILISATION', desc: 'Moto immobilisée, pied posé au moment nécessaire.' },
        { time: '3', label: 'OBSERVATION', desc: 'Je regarde autour de moi avant de repartir.' },
        { time: '4', label: 'REDÉMARRAGE', desc: 'Point de patinage, motricité progressive, repartir proprement.' },
      ],
      retenir: 'TU COMMENCES À CHOISIR L\'ENDROIT OÙ TA MOTO S\'ARRÊTE.',
    },
    {
      num: '11',
      kicker: 'Fluidité',
      title: 'Tu enchaînes sans te désorganiser',
      lead: "Jusqu'ici, nous avons beaucoup travaillé compétence par compétence. Maintenant, je vais commencer à les associer : démarrage → allure lente → changement de direction → porte → demi-tour → retour → arrêt. Ce qui m'intéresse n'est pas seulement chaque difficulté, mais ce qui se passe entre deux difficultés.",
      type: 'list',
      list: [
        { label: 'ATTENTION', desc: 'Est-ce que tu relâches ton attention entre deux difficultés ?' },
        { label: 'REGARD', desc: 'Est-ce que ton regard retombe une fois la porte franchie ?' },
        { label: 'MOTRICITÉ', desc: 'Est-ce que tu coupes toute ta motricité entre deux exercices ?' },
        { label: 'PRÉCIPITATION', desc: 'Est-ce que tu te précipites vers l\'exercice suivant ?' },
      ],
      retenir: 'UN BON ENCHAÎNEMENT N\'EST PAS UNE SUCCESSION DE GESTES BRUTAUX.',
    },
    {
      num: '12',
      kicker: 'Transitions d\'allure',
      title: 'Tu augmentes progressivement ton allure',
      lead: "Nous quittons maintenant ponctuellement la très basse allure. Tu accélères davantage, tu passes les rapports lorsque cela est nécessaire, tu stabilises ta trajectoire, puis tu réduis ton allure. Ta moto change de comportement lorsque l'allure augmente : tu dois t'adapter sans perdre ton regard, ta trajectoire, ta souplesse ni ta maîtrise des commandes.",
      type: 'timeline',
      steps: [
        { time: '1', label: 'LENT', desc: 'Allure lente stable, motricité continue.' },
        { time: '2', label: 'ACCÉLÉRATION', desc: 'Ouverture progressive, sans à-coup.' },
        { time: '3', label: 'ALLURE PLUS ÉLEVÉE', desc: 'Trajectoire stabilisée, regard porté plus loin.' },
        { time: '4', label: 'DÉCÉLÉRATION', desc: 'Freinage progressif, embrayage préparé.' },
        { time: '5', label: 'RETOUR AU LENT', desc: 'Moto de nouveau stable à basse allure.' },
      ],
      retenir: 'CHANGER D\'ALLURE SANS PERDRE NI TON REGARD NI TA SOUPLESSE.',
    },
    {
      num: '13',
      kicker: 'Freinage progressé',
      title: 'Tu progresses sur le freinage',
      lead: "Nous allons plus loin que lors de la première séance. Tu connais déjà le principe : avant de freiner, regarde loin et stabilise ta moto. Puis tu déclenches ton freinage et tu apprends à augmenter progressivement ton action, tout en restant attentif au comportement de la moto. Nous faisons évoluer l'allure et l'intensité progressivement en fonction de ta maîtrise.",
      type: 'list',
      list: [
        { label: 'EFFICACITÉ', desc: 'Une distance d\'arrêt cohérente avec ton action.' },
        { label: 'PROGRESSIVITÉ', desc: 'Une augmentation d\'intensité, jamais une action brutale.' },
        { label: 'STABILITÉ', desc: 'La moto reste droite et équilibrée pendant le freinage.' },
        { label: 'PRÉCISION', desc: 'Tu freines quand tu l\'as décidé, pas par surprise.' },
      ],
      retenir: 'UN FREINAGE EFFICACE DOIT RESTER UN FREINAGE CONTRÔLÉ.',
    },
    {
      num: '14',
      kicker: 'Bilan Plateau N°2',
      title: 'Ton bilan plateau n°2',
      lead: "Nous arrêtons la moto. Comme pour chaque séance, tu analyses avant moi : comment t'es-tu senti aujourd'hui ? Puis nous reprenons les compétences avec trois niveaux d'appréciation.",
      type: 'bilan',
      questions: [
        'Retrouver rapidement ton point de patinage ?',
        'Conserver une motricité régulière ?',
        'Coordonner tes commandes à basse allure ?',
        'Utiliser ton regard pour préparer la suite ?',
        'Conserver une allure lente stable ?',
        'Changer de direction sans te crisper ?',
        'Réaliser un demi-tour avec davantage de précision ?',
        'Laisser la moto s\'orienter sans lutter contre elle ?',
        'Franchir plusieurs portes successivement ?',
        'T\'arrêter précisément ?',
        'Redémarrer proprement ?',
        'Enchaîner plusieurs difficultés ?',
        'Augmenter ton allure en restant maître de ta moto ?',
        'Réaliser un freinage plus efficace et progressif ?',
      ],
      blocks: [
        { title: '🟢 JE MAÎTRISE DE PLUS EN PLUS', color: 'green' },
        { title: '🟠 JE RÉUSSIS MAIS PAS ENCORE RÉGULIÈREMENT', color: 'amber' },
        { title: '🔴 J\'AI ENCORE BESOIN D\'AIDE', color: 'red' },
      ],
      body: "Puis nous définissons : ce qui est devenu plus facile · ce qui reste fragile · ce que tu as compris aujourd'hui · ton objectif pour la session N°3. Lors de la première séance, tu as découvert comment agir sur ta moto. Aujourd'hui, tu commences à comprendre comment coordonner toutes ces actions. Progressivement, la chaîne devient naturelle : regard → motricité → dosage → équilibre → trajectoire → fluidité.",
      retenir: 'PLUS TES COMMANDES DEVIENNENT NATURELLES, PLUS TON ATTENTION PEUT SE LIBÉRER POUR PILOTER TA MOTO.',
    },
  ],
};
